import React, { useCallback, useEffect, useState } from 'react'
import formStyles from 'modules/Forms/common-form.module.scss'
import GridSpinner from 'components/UI/GridSpinner'
import { formatToString } from 'utils/formatMoney'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import validator from 'utils/asyncValidator'
import * as Yup from 'yup'
import { useParams } from 'react-router-dom'
import pick from 'lodash/pick'
import head from 'lodash/head'
import { Alert, Button, Typography } from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import ButtonBack from 'components/FormControls/back-button'
import styles from 'components/FormControls/form-controls.module.scss'
import PropTypes from 'prop-types'
import renderAmountField from 'components/FormControls/amountField'
import renderSelect from 'components/FormControls/select'
import oneOfMapper from 'utils/validators/oneOfMapper'
import { CURRENCIES } from 'constants/app-config'

const validationSchema = Yup.object().shape({
  from: Yup.number(),
  paymentAmount: Yup.string()
    .test(
      'notZero',
      'agency::transfer_form.validation_messages.greater_than_zero',
      function test(value) {
        const { path, createError } = this
        value = +value

        if (isNaN(value)) {
          return createError({
            path,
            message: 'agency::transfer_form.validation_messages.not_correct_amount',
          })
        }

        return (
          value > 0 ||
          createError({
            path,
            message: 'agency::transfer_form.validation_messages.greater_than_zero',
          })
        )
      },
    )
    .required('common::validation_messages.required'),
  currency: Yup.mixed().oneOf(CURRENCIES, ({ values }) =>
    oneOfMapper(values, 'common::validation_messages.oneOf', `common::currencies.`),
  ),
})

const selector = formValueSelector('TransferToClient')

let TransferToClient = ({
  handleSubmit,
  submitting,
  change,
  error,
  //blur,
  invalid,
}) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [wallets, setWallets] = useState([])

  const { formValues, user } = useSelector((state) => ({
    formValues: selector(state, 'wallet', 'from', 'paymentAmount', 'currency'),
    user: state['app'].user,
  }))

  const submit = useCallback(
    (values, dispatch) =>
      dispatch({
        type: 'AGENCY/FORMS_TRANSFER_TO_CLIENT',
        payload: {
          id: id,
          data: pick(values, ['from', 'currency', 'paymentAmount']),
        },
      }),
    [id],
  )

  useEffect(() => {
    if (!user.profiles || user.profiles.length === 0) return

    const profileWallets = user.profiles.reduce((acc, profile) => {
      acc = acc.concat(
        profile.wallets.map((wallet) => ({
          value: wallet.id,
          currency: wallet.currency,
          label: `${t(`common::profile_types.${profile.type}`)}: ${formatToString(
            wallet.value,
            wallet.currency,
          )}`,
        })),
      )
      return acc
    }, [])

    setWallets(profileWallets)

    if (profileWallets.length > 0) {
      change('wallet', head(profileWallets).value)
    }
  }, [user, setWallets])

  useEffect(() => {
    if (!formValues.wallet || !user) return

    const targetProfile = user.profiles.reduce((obj, profile) => {
      const targetWallet = profile.wallets.find((wallet) => wallet.id === formValues.wallet)
      if (targetWallet) {
        Object.assign(obj, { ...profile, wallets: [targetWallet] })
      }
      return obj
    }, {})

    if (targetProfile) {
      Object.prototype.hasOwnProperty.call(targetProfile, 'id') && change('from', targetProfile.id)

      if (
        Object.prototype.hasOwnProperty.call(targetProfile, 'wallets') &&
        targetProfile.wallets.length > 0
      ) {
        change('currency', head(targetProfile.wallets).currency)
      }
    }
  }, [formValues.wallet, user, wallets])

  return !user ? (
    <GridSpinner />
  ) : (
    <div className={formStyles['formContainer']}>
      <form onSubmit={handleSubmit(submit)}>
        {error && (
          <Alert variant="filled" severity="error">
            {t(error)}
          </Alert>
        )}

        <div className={'margin-bottom-16'}>
          <Field
            label={t('agency::transfer_form.controls.from')}
            fullWidth
            name="wallet"
            component={renderSelect}
            options={wallets}
            normalize={(value = '') => value}
            t={t}
            readOnly={wallets.length === 0}
            ControlProps={{
              startAdornment: wallets.length === 0 && <GridSpinner width={24} />,
            }}
          />
        </div>

        <div className={'margin-bottom-16'}>
          <Field
            fullWidth
            name="paymentAmount"
            type="text"
            component={renderAmountField}
            t={t}
            label={t('agency::transfer_form.controls.payment_amount')}
            autocomplete="off"
            format={(value) => (value === null ? '' : '' + value)}
            parse={(value) => value.replace(/\s/g, '').replace(',', '.')}
            InputProps={{
              className: styles['input-field'],
              endAdornment: formValues.currency && (
                <Typography variant={'Body-25'} noMargin color={'Label-Secondary'}>
                  {formValues.currency}
                </Typography>
              ),
            }}
          />
        </div>

        <div className={formStyles['formControl__action-buttons']}>
          <ButtonBack />
          <Button disabled={invalid || submitting} size="l" variant="primary" type="submit">
            {t('agency::transfer_form.controls.transfer_button')}
          </Button>
        </div>
      </form>
    </div>
  )
}

TransferToClient = reduxForm({
  form: 'TransferToClient',
  initialValues: {
    wallet: null,
    from: null,
    paymentAmount: '0.00',
    currency: '',
  },
  asyncValidate: validator(validationSchema),
})(TransferToClient)

TransferToClient.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  change: PropTypes.func,
  error: PropTypes.string,
  blur: PropTypes.func,
  invalid: PropTypes.bool,
}

export default TransferToClient
